/**
 * Safe extraction of Cognito `custom:accountId` from an Amplify auth session.
 * `session.tokens` is undefined when the user is unauthenticated or the session
 * has expired (Sentry JEPTO-25 / JEP-10337).
 * Tenant claims come from the access token (JEP-10356).
 */
export function getAccountIdFromAuthSession(
	session: {
		tokens?: {
			accessToken?: { payload?: Record<string, unknown> };
		};
	} | null
): string | null {
	const accountId =
		session?.tokens?.accessToken?.payload?.['custom:accountId'];
	if (accountId == null) {
		return null;
	}
	return String(accountId);
}

/**
 * Safe extraction of the `email` claim from a Cognito ID token payload.
 * Some accounts or token shapes omit `email`; avoid throwing on `.toString()` of undefined.
 */
export function getEmailFromIdTokenPayload(payload: unknown): string | null {
	if (payload == null || typeof payload !== 'object') {
		return null;
	}
	const record = payload as Record<string, unknown>;
	const email = record['email'];
	if (email == null) {
		return null;
	}
	return String(email);
}

/**
 * Access tokens expose `username`; many callers still read `cognito:username`
 * from the cached payload (JEP-10356). Copy `username` onto that key.
 */
export function normalizeAccessTokenIdentityClaims(
	payload: Record<string, unknown> | undefined
): Record<string, unknown> | undefined {
	if (!payload) {
		return undefined;
	}
	const username = payload['cognito:username'] ?? payload['username'];
	if (username == null) {
		return { ...payload };
	}
	return {
		...payload,
		'cognito:username': username,
	};
}
